/* eslint-disable react-hooks/rules-of-hooks */
import { useMemo, useState } from 'react'
import {
  useRecoilRefresher_UNSTABLE,
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from 'recoil'
import type Stripe from 'stripe'
import styled from '@emotion/styled'
import { Link, Typography } from '@mui/material'
import { Colors, Shapes } from 'src/components/theme'
import { ButtonWithIcon } from 'src/components/button/button-with-icon'
import { Spinner } from 'src/components/spinner'
import { browserLogger } from 'src/utils/browser-logger'
import { paymentsClient } from 'src/clients'
import { AlertHelp } from 'src/components/alert/alert-help'
import { FlexColumn } from 'src/components/flex-containers'
import { formatVal } from 'src/utils/number-format'
import { SUBSCRIPTION_MODAL_CONTENT_DIMENSIONS } from 'src/constants/subscriptions'
import { SubscriptionPlanButton } from 'src/views/subscriptions/plan-button'
import { SubscriptionModalHeader } from 'src/views/subscriptions/header'
import { SubscriptionModalFooter } from 'src/views/subscriptions/footer'
import { SubscriptionManagePaymentMethods } from 'src/views/subscriptions/manage-payment-method'
import { SUBSCRIPTION_UPDATE_STEPS } from 'src/atoms/payments'

import * as paymentsAtoms from 'src/atoms/payments'
import * as billingAtoms from 'src/atoms/billing'
import * as userAtoms from 'src/atoms/user'

const Wrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${Shapes.spacing[2]}px;
  width: ${SUBSCRIPTION_MODAL_CONTENT_DIMENSIONS.width}px;
  max-width: ${SUBSCRIPTION_MODAL_CONTENT_DIMENSIONS.width}px;
  margin: 0 auto;
`
const PlansWrapper = styled.div`
  display: flex;
  gap: ${Shapes.spacing[4]}px;
`
const SupportLink = styled(Link)`
  color: ${Colors.textDark};
  text-decoration: underline;
  font-weight: 700;
  :hover {
    color: ${Colors.textBlack};
  }
`

export const SelectedPlan = () => {
  const setSubscriptionUpdateStep = useSetRecoilState(paymentsAtoms.subscriptionUpdateStep)
  const subscriptionType = useRecoilValue(paymentsAtoms.subscriptionType)
  const selectedPlan = useRecoilValue(paymentsAtoms.selectedPaymentPlan)
  const [selectedPlanPrice, setSelectedPlanPrice] = useRecoilState(paymentsAtoms.selectedPaymentPlanPrice)
  const previewProration = useRecoilValue(paymentsAtoms.previewProration)
  const setUpdatedSubscription = useSetRecoilState(paymentsAtoms.updatedSubscription)
  const resetSelectedPlanPrice = useResetRecoilState(paymentsAtoms.selectedPaymentPlanPrice)
  const refreshUser = useRecoilRefresher_UNSTABLE(userAtoms.currentUser)
  const refreshPaymentMethods = useRecoilRefresher_UNSTABLE(billingAtoms.paymentMethods)
  const [isLoading, setIsLoading] = useState(false)
  const isChangePaymentMethodComplete = useRecoilValue(billingAtoms.isChangePaymentMethodComplete)

  const prorationInfo = useMemo(() => {
    if (!previewProration) {
      return undefined
    }
    // Total to be paid to switch plans
    const total = previewProration.total / 100
    // Unused credit based on current plan
    const credit = (previewProration.lines.data[0].amount / 100) * -1
    return {
      total,
      credit,
    }
  }, [previewProration])

  const payTitle = useMemo(() => {
    if (!prorationInfo) {
      return undefined
    }
    if (prorationInfo.total > 0) {
      return `Pay ${formatVal(prorationInfo.total, 'currency-expanded')} today`
    }
    return `Pay $0.00 today`
  }, [prorationInfo])

  const onSelectPlanPrice = (price: Stripe.Price) => () => setSelectedPlanPrice(price)

  const onBack = async () => {
    resetSelectedPlanPrice()
    setSubscriptionUpdateStep(SUBSCRIPTION_UPDATE_STEPS.PLANS)
  }

  const onClick = async () => {
    // Used to upgrade/downgrade an existing subscription (proration)
    // Only update a subscription if the user is already subscribed
    if (!subscriptionType || subscriptionType === 'new' || !selectedPlanPrice) {
      return undefined
    }
    try {
      setIsLoading(true)

      const updatedSubscription = await paymentsClient.updateSubscription({
        priceId: selectedPlanPrice.id,
      })
      setUpdatedSubscription(updatedSubscription)
      setSubscriptionUpdateStep(SUBSCRIPTION_UPDATE_STEPS.CONFIRMATION)
      // Refresh user to update the user's tier
      refreshUser()
      refreshPaymentMethods()
    } catch (err) {
      browserLogger.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Wrapper>
      <FlexColumn gap={Shapes.spacing[4]}>
        <SubscriptionModalHeader>
          <Typography variant="header2" color={Colors.textBlack}>
            {selectedPlan?.name} Plan
          </Typography>
          <Typography variant="textmdm" color={Colors.textLight}>
            Fill out the form below to upgrade your subscription
          </Typography>
        </SubscriptionModalHeader>
        {prorationInfo && (
          <AlertHelp
            color="warning"
            message={
              prorationInfo.total > 0 ? (
                <Typography variant="textmdm">
                  Please note that you will be charged{' '}
                  <strong>{formatVal(prorationInfo.total, 'currency-expanded')}</strong> today because you have{' '}
                  <strong>{formatVal(prorationInfo.credit, 'currency-expanded')}</strong> left in remaining credit from
                  your current plan. For any clarification or assistance, feel free to reach out to our{' '}
                  <SupportLink href="">Customer Support</SupportLink> team.
                </Typography>
              ) : (
                <Typography variant="textmdm">
                  Please note that you won't be charged anything today because you have{' '}
                  <strong>{formatVal(prorationInfo.credit, 'currency-expanded')}</strong> left in remaining credit from
                  your current plan. For any clarification or assistance, feel free to reach out to our{' '}
                  <SupportLink href="">Customer Support</SupportLink> team.
                </Typography>
              )
            }
          />
        )}
        <PlansWrapper>
          {selectedPlan &&
            selectedPlan.prices.map((price) => (
              <SubscriptionPlanButton
                key={price.id}
                active={!!selectedPlanPrice && price.id === selectedPlanPrice.id}
                price={price}
                onClick={onSelectPlanPrice(price)}
              />
            ))}
        </PlansWrapper>
        <div>
          <SubscriptionManagePaymentMethods />
        </div>
        <Typography marginTop={`${Shapes.spacing[2]}px`} variant="textmdm" color={Colors.textLight}>
          By providing your card information, you allow AlphaMap to charge your card for future payments in accordance
          with their terms.
        </Typography>
      </FlexColumn>

      <SubscriptionModalFooter>
        <ButtonWithIcon disabled={isLoading || !isChangePaymentMethodComplete} onClick={onBack}>
          Back
        </ButtonWithIcon>
        <ButtonWithIcon type="submit" inverted disabled={isLoading || !isChangePaymentMethodComplete} onClick={onClick}>
          {isLoading ? (
            <Spinner
              style={{
                color: `${Colors.textWhite}`,
              }}
              size={16}
            />
          ) : (
            payTitle || 'Pay'
          )}
        </ButtonWithIcon>
      </SubscriptionModalFooter>
    </Wrapper>
  )
}
